class EtsyProfitCalculator {
  constructor() {
    // Form elements
    this.form = document.getElementById('etsy-profit-form');
    this.purchasePrice = document.getElementById('etsy-purchase-price');
    this.salePrice = document.getElementById('etsy-sale-price');
    this.customTaxInput = document.getElementById('etsy-custom-tax');
    this.shippingCost = document.getElementById('etsy-shipping-cost');
    
    // Tax buttons
    this.taxButtons = document.querySelectorAll('.etsy-tax-btn');
    this.customTaxContainer = document.querySelector('.etsy-custom-tax');
    
    // Radio buttons
    this.annualRevenue = document.querySelectorAll('input[name="annual-revenue"]');
    
    // Tax toggle switches
    this.purchasePriceTaxInclusive = document.getElementById('purchase-price-tax-inclusive');
    this.shippingCostTaxInclusive = document.getElementById('shipping-cost-tax-inclusive');
    
    // Checkbox
    this.includeOffsiteAds = document.getElementById('include-offsite-ads');
    
    // Results elements
    this.noResults = document.querySelector('.phone-no-results');
    this.hasResults = document.querySelector('.phone-has-results');
    
    // Result display elements
    this.salePriceIncTax = document.getElementById('sale-price-inc-tax');
    this.salePriceExcTax = document.getElementById('sale-price-exc-tax');
    this.purchasePriceDisplay = document.getElementById('purchase-price');
    this.transactionFee = document.getElementById('transaction-fee');
    this.transactionFeeRate = document.getElementById('transaction-fee-rate');
    this.paymentProcessing = document.getElementById('payment-processing');
    this.listingFee = document.getElementById('listing-fee');
    this.offsiteAdsFee = document.getElementById('offsite-ads-fee');
    this.offsiteAdsRow = document.getElementById('offsite-ads-row');
    this.shippingAmount = document.getElementById('shipping-amount');
    this.netProfit = document.getElementById('net-profit');
    this.profitMargin = document.getElementById('profit-margin');
    
    // Etsy fee constants (2025 rates)
    this.ETSY_FEES = {
      transactionFee: 6.5,        // 6.5% of total sale (including shipping)
      paymentProcessing: 3.0,     // 3% of total sale
      paymentFixed: 0.25,         // $0.25 fixed fee per order
      listingFee: 0.20,           // $0.20 per listing (4 months)
      offsiteAds: {
        lowVolume: 15,            // 15% for <$10k annual revenue (can opt out)
        highVolume: 12,           // 12% for >$10k annual revenue (mandatory)
        maxPerOrder: 100          // $100 max per order
      },
      currencyConversion: 2.5     // 2.5% if listing/payment currency differs
    };
    
    // Current tax rate
    this.currentTaxRate = 8.5;
    
    this.init();
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    // Form submission
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.calculateProfit();
    });

    // Tax button selection
    this.taxButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.selectTaxRate(btn);
      });
    });

    // Custom tax input
    this.customTaxInput.addEventListener('input', () => {
      this.currentTaxRate = parseFloat(this.customTaxInput.value) || 0;
    });

    // Offsite ads checkbox change
    this.includeOffsiteAds.addEventListener('change', () => {
      this.toggleOffsiteAdsDisplay();
    });
  }

  selectTaxRate(selectedBtn) {
    // Remove active class from all buttons
    this.taxButtons.forEach(btn => btn.classList.remove('active'));
    
    // Add active class to selected button
    selectedBtn.classList.add('active');
    
    const taxValue = selectedBtn.dataset.tax;
    
    if (taxValue === 'custom') {
      this.customTaxContainer.style.display = 'block';
      this.currentTaxRate = parseFloat(this.customTaxInput.value) || 8.5;
    } else {
      this.customTaxContainer.style.display = 'none';
      this.currentTaxRate = parseFloat(taxValue);
    }
  }

  toggleOffsiteAdsDisplay() {
    if (this.offsiteAdsRow) {
      this.offsiteAdsRow.style.display = this.includeOffsiteAds.checked ? 'flex' : 'none';
    }
  }

  calculateProfit() {
    // Get form values
    const purchasePriceInput = parseFloat(this.purchasePrice.value) || 0;
    const salePrice = parseFloat(this.salePrice.value) || 0;
    const shippingCostInput = parseFloat(this.shippingCost.value) || 0;
    const annualRevenue = document.querySelector('input[name="annual-revenue"]:checked')?.value || 'low';
    
    // Check tax inclusion toggles
    const isPurchasePriceTaxInclusive = this.purchasePriceTaxInclusive.checked;
    const isShippingCostTaxInclusive = this.shippingCostTaxInclusive.checked;
    const includeOffsiteAds = this.includeOffsiteAds.checked;

    // Validate inputs
    if (purchasePriceInput <= 0 || salePrice <= 0) {
      this.showNoResults();
      return;
    }

    // Calculate purchase price excluding tax
    const purchasePrice = isPurchasePriceTaxInclusive 
      ? purchasePriceInput / (1 + this.currentTaxRate / 100)
      : purchasePriceInput;

    // Calculate shipping cost excluding tax
    const shippingCostBase = isShippingCostTaxInclusive 
      ? shippingCostInput / (1 + this.currentTaxRate / 100)
      : shippingCostInput;

    // Calculate tax amounts for sale price
    const salePriceExcludingTax = salePrice / (1 + this.currentTaxRate / 100);
    const salePriceIncludingTax = salePrice;

    // Calculate total sale for Etsy fees (sale price + shipping)
    const totalSale = salePrice + shippingCostInput;

    // Calculate Etsy fees
    const transactionFeeAmount = totalSale * (this.ETSY_FEES.transactionFee / 100);
    const paymentProcessingAmount = (totalSale * (this.ETSY_FEES.paymentProcessing / 100)) + this.ETSY_FEES.paymentFixed;
    const listingFeeAmount = this.ETSY_FEES.listingFee;

    // Calculate offsite ads fee if applicable
    let offsiteAdsFeeAmount = 0;
    if (includeOffsiteAds) {
      const adsRate = annualRevenue === 'high' ? this.ETSY_FEES.offsiteAds.highVolume : this.ETSY_FEES.offsiteAds.lowVolume;
      offsiteAdsFeeAmount = totalSale * (adsRate / 100);
      // Apply max cap of $100 per order
      if (offsiteAdsFeeAmount > this.ETSY_FEES.offsiteAds.maxPerOrder) {
        offsiteAdsFeeAmount = this.ETSY_FEES.offsiteAds.maxPerOrder;
      }
    }

    // Calculate net profit
    const netProfit = salePriceExcludingTax - purchasePrice - transactionFeeAmount - paymentProcessingAmount - listingFeeAmount - offsiteAdsFeeAmount - shippingCostBase;

    // Calculate profit margin
    const profitMargin = salePriceExcludingTax > 0 ? (netProfit / salePriceExcludingTax) * 100 : 0;

    // Display results
    this.displayResults({
      salePriceIncTax: salePriceIncludingTax,
      salePriceExcTax: salePriceExcludingTax,
      purchasePrice: purchasePrice, // Show the calculated purchase price (tax excluded)
      transactionFee: transactionFeeAmount,
      transactionFeeRate: this.ETSY_FEES.transactionFee, // Always show the fixed 6.5% rate
      paymentProcessing: paymentProcessingAmount,
      listingFee: listingFeeAmount,
      offsiteAdsFee: offsiteAdsFeeAmount,
      shippingAmount: shippingCostBase, // Show the calculated shipping cost (tax excluded)
      netProfit: netProfit,
      profitMargin: profitMargin
    });
  }

  displayResults(results) {
    // Update result values
    this.salePriceIncTax.textContent = this.formatCurrency(results.salePriceIncTax);
    this.salePriceExcTax.textContent = this.formatCurrency(results.salePriceExcTax);
    this.purchasePriceDisplay.textContent = '- ' + this.formatCurrency(results.purchasePrice);
    this.transactionFee.textContent = '- ' + this.formatCurrency(results.transactionFee);
    // Update transaction fee rate display
    this.transactionFeeRate.textContent = `(${results.transactionFeeRate}%)`;
    this.paymentProcessing.textContent = '- ' + this.formatCurrency(results.paymentProcessing);
    this.listingFee.textContent = '- ' + this.formatCurrency(results.listingFee);
    this.offsiteAdsFee.textContent = results.offsiteAdsFee > 0 ? '- ' + this.formatCurrency(results.offsiteAdsFee) : '$0.00';
    this.shippingAmount.textContent = results.shippingAmount > 0 ? '- ' + this.formatCurrency(results.shippingAmount) : '$0.00';
    
    // Format profit/loss
    if (results.netProfit >= 0) {
      this.netProfit.textContent = this.formatCurrency(results.netProfit);
      this.netProfit.style.color = '#10b981'; // Green for profit
    } else {
      this.netProfit.textContent = '- ' + this.formatCurrency(Math.abs(results.netProfit));
      this.netProfit.style.color = '#ef4444'; // Red for loss
    }
    
    // Format margin
    if (results.profitMargin >= 0) {
      this.profitMargin.textContent = results.profitMargin.toFixed(2) + ' %';
      this.profitMargin.style.color = '#f1641e'; // Etsy orange
    } else {
      this.profitMargin.textContent = results.profitMargin.toFixed(2) + ' %';
      this.profitMargin.style.color = '#ef4444'; // Red for negative margin
    }

    // Toggle offsite ads row visibility
    this.toggleOffsiteAdsDisplay();

    // Show results
    this.showResults();
  }

  showResults() {
    this.noResults.style.display = 'none';
    this.hasResults.style.display = 'block';
  }

  showNoResults() {
    this.noResults.style.display = 'block';
    this.hasResults.style.display = 'none';
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(Math.abs(amount));
  }

  // Utility method to validate form
  validateForm() {
    const purchasePrice = parseFloat(this.purchasePrice.value) || 0;
    const salePrice = parseFloat(this.salePrice.value) || 0;
    
    return purchasePrice > 0 && salePrice > 0;
  }

  // Method to get current calculation state
  getCurrentState() {
    return {
      purchasePrice: parseFloat(this.purchasePrice.value) || 0,
      salePrice: parseFloat(this.salePrice.value) || 0,
      taxRate: this.currentTaxRate,
      shippingCost: parseFloat(this.shippingCost.value) || 0,
      annualRevenue: document.querySelector('input[name="annual-revenue"]:checked')?.value || 'low',
      purchasePriceTaxInclusive: this.purchasePriceTaxInclusive.checked,
      shippingCostTaxInclusive: this.shippingCostTaxInclusive.checked,
      includeOffsiteAds: this.includeOffsiteAds.checked
    };
  }
}